import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { Loader, ToastProvider } from '@jutro/components';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { WniCommonQuoteService } from 'wni-capability-quoteandbind';
import gatewayMessages from 'gw-capability-gateway-react/gateway.messages';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import useJutroMFESdk from './Hooks/useJutroMFESdk';
import messages from './SubmissionIntakeReview.messages';
import styles from './SubmissionIntakeReview.module.scss';
import SubmissionIntakeUtil from './SubmissionIntakeUtil';
import useWniModal from '../../hooks/ModalNext/useWniModal';
import { ThemeContext, ThemeConfigContext } from '@jutro/legacy/theme';

import { TranslatorContext } from '@jutro/legacy/locale';

interface AuthProps {
    idToken: string;
    accessToken: string;
    userInfo: { [key: string]: unknown } | undefined;
}

export const SubmissionIntakeReview: React.FC = (props) => {
    const history = useHistory();

    const {
        acrodReviewUrl,
        urlPrefix,
        deploymentId,
        auth = useAuthentication(),
        callbackMap,
        submissionTokenId,
        extendProps: {
            jobID
        } = {}
    } = props;

    const { submissionIntakeURL, lifecycleserviceURL } = SubmissionIntakeUtil.getConfigs();

    const translator = useContext(TranslatorContext);
    const mfeSdk = useJutroMFESdk(submissionIntakeURL);
    const containerRef = useRef<HTMLDivElement>();
    const root = useMemo(
        () => mfeSdk?.createRoot(containerRef.current, submissionIntakeURL),
        [mfeSdk]
    );
    const [mfeLoaded, setMfeLoaded] = useState<boolean>(false);
    const { switchTheme } = useContext(ThemeContext);
    const themeConfig = useContext(ThemeConfigContext);
    const redirectPath = `${history.location.pathname}${history.location.search}`;
    const authProps: AuthProps = {
        idToken: auth?.idToken,
        accessToken: auth?.accessToken,
        userInfo: auth?.userInfo,
    };

    const location = acrodReviewUrl.substring(acrodReviewUrl.indexOf(urlPrefix))

    const [locationChanged, updateLocationChanged] = useState(false);
    const [newLocation, updateNewLocation] = useState(null);
    const modalApi = useWniModal();
    const myHeaders = new Headers();
    const sigwrequestgrn = deploymentId;
    const siurl = lifecycleserviceURL;
    const token = auth.accessToken;
    myHeaders.append('Authorization', `Bearer ${token}`);
    myHeaders.append('GW-Request-GRN', sigwrequestgrn);
    myHeaders.append('GW-User', auth.userInfo.sub);
    
    const handleError = async (title, message) => {
        return modalApi.showAlert({
            title: title,
            message: message,
            status: 'error',
            icon: 'gw-error-outline',
            confirmButtonText: commonMessages.ok,
        });
    };


    let intervalCall;
    const getStatusSubmissionIntake = async () => {
        try {
            await fetch(
                `${siurl}/${submissionTokenId}/status`,
                {
                    method: 'GET',
                    headers: myHeaders
                }
            ).then((res) => {
                if (res.ok) {
                    return res.json();
                }
            }).then(async (jsondata) => {
                if (jsondata.status === "PCSubmitting" && !_.isNil(jobID)) {
                    const timeoutFlag = await WniCommonQuoteService.getTimeoutFlagStatus(jobID, auth.authHeader);
                    const {isServiceCallInProgress, thresholdExceeded} = timeoutFlag;
                    if (isServiceCallInProgress || thresholdExceeded) {
                        await handleError(
                            gatewayMessages.modalError,
                            messages.serviceCallInProgressError
                        );
                        clearInterval(intervalCall);
                        const state = {
                            pathname: `/quotes/${jobID}/summary`
                        };
                        history.push(state);
                        window.location.reload();
                        return false;
                    }
                    if (jsondata.status === "PCSubmitted") {
                        clearInterval(intervalCall);
                    }
                }
            })
        }
        catch (e) {
            // clean up
            clearInterval(intervalCall);
            return modalApi.showAlert({
                title: 'ACORD Upload',
                message: 'Something wrong with subIntake status API call!',
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: "Ok"
            }).then(() => {
                window.location.reload();
            })
            .catch(_.noop);
        }
    };

    useEffect(() => {
        intervalCall = setInterval(() => {
            getStatusSubmissionIntake();
        }, 5000);
        return () => {
            // clean up
            clearInterval(intervalCall);
        };
    }, []);

    const renderMfe = () => {
        root?.render(
            {
                deploymentId: deploymentId,
                auth: authProps,
                jutro: {
                    router: {
                        location: newLocation || location,
                        onLocationChange: (loc, action) => {
                            updateLocationChanged(true);
                            updateNewLocation(loc);
                        },
                    },
                    mode: 'moduleFederation',
                    integrateJutro: true, // integrate all the features below, true when using MicroFrontend component (no matter if it's mode moduleFederation or iframe). For SDK, it's false by default.
                    integrateAuth: true, // integrate Jutro authentication
                    integrateG11n: true, // integrate Jutro globalization
                    integrateTheme: true, // integrate Jutro theme
                    integrateToast: true, // integrate Jutro toasts
                    integrateModal: true, // integrate Jutro modals
                    integrateActions: true,
                    integrateRouter: true,
                    auth: authProps,
                    theme: {
                        themeConfig,
                        switchTheme,
                    },
                    configOverrides: {
                        shellAppSettings: {
                            embedded: true,
                            deploymentId,
                            redirectPath,
                            submissionCreatedCallback: async ({
                                submissionNumber,
                            }) => {
                                if (_.isFunction(_.get(callbackMap, 'submissionCreatedCallback'))) {
                                    await callbackMap.submissionCreatedCallback(submissionNumber);
                                } else {
                                    ToastProvider.toast({
                                        autoClose: false,
                                        autoFocus: false,
                                        message: `Created submissions: ${submissionNumber}`,
                                        type: 'success',
                                    });
                                    let jumpSubmissionNumber = submissionNumber;
                                    // for arrayList, get the first item
                                    if (_.get(jumpSubmissionNumber, 'length') === 1) {
                                        jumpSubmissionNumber = _.get(jumpSubmissionNumber, '0');
                                    }
                                    // jump to the first submission when result with multiple submissions
                                    if (_.get(jumpSubmissionNumber, 'length') > 10) {
                                        jumpSubmissionNumber = jumpSubmissionNumber.substring(0, 10);
                                    }
                                    history.push(
                                        `/quotes/${jumpSubmissionNumber}/summary`
                                    );
                                }
                            },
                            uploadFailCallback: async () => {
                                ToastProvider.toast({
                                    autoClose: 5000,
                                    autoFocus: false,
                                    message: `${translator(
                                        messages.submissionUploadFailed
                                    )}`,
                                    type: 'error',
                                });
                                if (_.isFunction(_.get(callbackMap, 'uploadFailCallback'))) {
                                    await callbackMap.uploadFailCallback();
                                } else {
                                    history.push('/')
                                }
                            },
                            deleteSubmissionCallback: async () => {
                                if (_.isFunction(_.get(callbackMap, 'deleteSubmissionCallback'))) {
                                    await callbackMap.deleteSubmissionCallback();
                                } else {
                                    history.push('/')
                                }
                            },
                            closeSubmissionCallback: async ({
                                submissionId,
                            }: {
                                submissionId: string;
                            }) => {
                                if (_.isFunction(_.get(callbackMap, 'closeSubmissionCallback'))) {
                                    await callbackMap.closeSubmissionCallback();
                                } else {
                                    ToastProvider.toast({
                                        autoClose: 5000,
                                        autoFocus: false,
                                        message: `${translator(
                                            messages.submissionClosed
                                        )} ${submissionId} `,
                                        type: 'success',
                                    });
                                    history.push('/')
                                }
                            },
                            spreadsheetImportedCallback: async ({
                                submissionNumber,
                            }) => {
                                if (_.isFunction(_.get(callbackMap, 'spreadsheetImportedCallback'))) {
                                    await callbackMap.spreadsheetImportedCallback();
                                } else {
                                    ToastProvider.toast({
                                        autoClose: 5000,
                                        autoFocus: false,
                                        message: `Spreadsheet Imported: ${submissionNumber}`,
                                        type: 'success',
                                    });
                                }
                            },
                            closeSpreadsheetCallback: async ({
                                submissionId,
                            }: {
                                submissionId: string;
                            }) => {
                                if (_.isFunction(_.get(callbackMap, 'closeSpreadsheetCallback'))) {
                                    await callbackMap.closeSpreadsheetCallback();
                                } else {
                                    ToastProvider.toast({
                                        autoClose: 5000,
                                        autoFocus: false,
                                        message: `${translator(
                                            messages.spreadsheetClosed
                                        )} ${submissionId} `,
                                        type: 'success',
                                    });
                                }
                            }
                        }
                    }
                }
            },
            {
                onLoaded: () => setMfeLoaded(true),
            }
        );
    };

    useEffect(() => {
        renderMfe();
    });

    useEffect(
        () => () => {
            setMfeLoaded(false);
            root?.unmount();
        },
        [root]
    );

    useEffect(
        () => () => {
            if (locationChanged) {
                updateLocationChanged(false);
                renderMfe();
            }

        },
        [locationChanged]
    );

    return (
        <div className={styles.frameContainer}>
            <div ref={containerRef} className={styles.frame} />
            {!mfeLoaded && (
                <div>
                    <Loader>
                        <div>
                            {translator(messages.loadingSubmissionIntake)}
                        </div>
                    </Loader>
                </div>
            )}
        </div>
    );
};

export default withRouter(SubmissionIntakeReview);
